import React from 'react';
import { Box, Button, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

import { dialogActionButtonSx, dialogActionsSx, StyledDialog } from 'theme/index';
import COLORS from 'theme/colors';

export interface AppointmentSummary {
  providerName: string;
  day: string;
  time: string;
  location?: string;
  consultationType?: string;
}

interface SelectionConfirmDialogProps {
  open: boolean;
  providerName: string;
  day: string;
  time: string;
  location?: string;
  // The consultation type (Virtual/In-person) the patient had selected when they picked this
  // slot - same filter value the free-slot fetch itself was scoped by, not read back from the
  // slot/appointment, so it reflects what the patient chose rather than being re-derived.
  consultationType?: string;
  onCancel: () => void;
  onContinue: () => void;
  // Overridden by ProviderAvailability's reschedule mode (see ManageAppointment) - rescheduling
  // an existing appointment reads differently from booking a brand new one, even though the
  // slot-selection UI and this confirm step are otherwise identical.
  title?: string;
  bodyPrefix?: string;
  // Reschedule mode only: the appointment being replaced. Rendered side by side with the newly
  // selected slot above instead of the plain one-line summary, so the patient can see exactly
  // what's changing before confirming.
  currentAppointment?: AppointmentSummary;
}

const AppointmentSummaryBox = ({ label, summary }: { label: string; summary: AppointmentSummary }) => (
  <Box
    sx={{
      border: `1px solid ${COLORS.Gray300}`,
      borderRadius: '8px',
      p: 1.5,
      // Side by side on wider screens; full-width stacked rows on mobile - at the dialog's own
      // maxWidth='xs' plus its default margin/padding, side-by-side here left each box only
      // ~120px wide on a real phone, too cramped to read providerName/day/time/location.
      width: { xs: '100%', sm: 'auto' },
      flex: { sm: 1 },
      minWidth: 0,
    }}
  >
    <Typography variant='body2' sx={{ color: COLORS.Gray600, fontWeight: 600, mb: 0.5 }}>
      {label}
    </Typography>
    <Typography variant='body2' sx={{ color: COLORS.Text, wordBreak: 'break-word' }}>
      {summary.providerName}
      <br />
      {summary.day} &middot; {summary.time}
      {summary.location ? <><br />{summary.location}</> : null}
      {summary.consultationType ? <><br />{summary.consultationType}</> : null}
    </Typography>
  </Box>
);

const SelectionConfirmDialog = ({
  open,
  providerName,
  day,
  time,
  location,
  consultationType,
  onCancel,
  onContinue,
  title = 'Confirm Appointment',
  bodyPrefix,
  currentAppointment,
}: SelectionConfirmDialogProps) => {
  // Folds consultationType into the sentence itself ("You selected an In-person appointment
  // with...") rather than appending it separately - only when bodyPrefix hasn't been overridden
  // (reschedule mode's currentAppointment branch below never reaches this sentence at all).
  const resolvedBodyPrefix =
    bodyPrefix ??
    (consultationType ? `You selected an ${consultationType} appointment with` : 'You selected an appointment with');

  return (
    <StyledDialog open={open} onClose={onCancel} maxWidth='xs' fullWidth>
      {/* StyledDialog's own Paper padding (theme/index.ts) stacks with DialogTitle's default
          16px 24px, leaving little width on a phone screen at the default h6 size - shrinking it
          on mobile keeps "Confirm Appointment"/"Confirm Reschedule" on one line instead of
          wrapping to two. */}
      <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>{title}</DialogTitle>
      <DialogContent>
        {currentAppointment ? (
          <>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mb: 2 }}>
              <AppointmentSummaryBox label='Current' summary={currentAppointment} />
              <AppointmentSummaryBox label='New' summary={{ providerName, day, time, location, consultationType }} />
            </Box>
            <Typography sx={{ color: COLORS.Text }}>Continue to confirm this change?</Typography>
          </>
        ) : (
          <Typography sx={{ color: COLORS.Text }}>
            {resolvedBodyPrefix} {providerName} on {day} &middot; {time}
            {location ? ` at ${location}` : ''}. Continue to confirm?
          </Typography>
        )}
      </DialogContent>
      {/* Stacked full-width on mobile, one row of two equal rounded buttons on sm+ - see
          dialogActionsSx/dialogActionButtonSx (shared with CancelAppointmentDialog). */}
      <DialogActions disableSpacing sx={dialogActionsSx}>
        <Button variant='outlined' onClick={onCancel} sx={dialogActionButtonSx}>
          Cancel
        </Button>
        <Button variant='contained' onClick={onContinue} sx={dialogActionButtonSx}>
          Continue
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default SelectionConfirmDialog;
